import { z } from 'zod';
import {
  BRAND_NAME_FORMATTING,
  APPROVED_SUBSTITUTIONS,
  AVOID_CROSS_CATEGORY_COMPARISONS,
  APPROVED_NUTRIENT_RELATED_PHRASES,
  AVOID_SPECIFIC_CLAIM_LANGUAGE,
  APPROVED_ON_PACKAGE_LANGUAGE,
  APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS,
  APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT,
  AVOID_RESTRICTED_TEXT_FOR_CLAIMS,
  GUIDELINE_TYPES
} from '../types/profileTypes';

// Define a schema for the modal form that includes temporary UI fields
export const ModalFormSchema = z.discriminatedUnion('type', [
  // Brand Name Formatting
  z.object({
    type: z.literal(BRAND_NAME_FORMATTING),
    name: z.string().min(1, 'Title is required'),
    parameters: z.object({
      format: z.string().min(1, 'Format is required'),
      brandName: z.string().min(1, 'Brand name is required'),
      condition: z.string().min(1, 'Condition is required'),
      customText: z.string().optional()
    }).catchall(z.unknown())
  }),
  
  // Approved Substitutions
  z.object({
    type: z.literal(APPROVED_SUBSTITUTIONS),
    name: z.string().min(1, 'Title is required'),
    parameters: z.object({
      approvedTerm: z.string().min(1, 'Approved term is required'),
      restrictedTerm: z.string().min(1, 'Restricted term is required')
    }).catchall(z.unknown())
  }),
  
  // Avoid Cross Category Comparisons
  z.object({
    type: z.literal(AVOID_CROSS_CATEGORY_COMPARISONS),
    name: z.string().min(1, 'Title is required'),
    parameters: z.object({
      comparisonType: z.string().min(1, 'Comparison type is required'),
      target: z.string().min(1, 'Target is required')
    }).catchall(z.unknown())
  }),
  
  // Approved Nutrient Related Phrases
  z.object({
    type: z.literal(APPROVED_NUTRIENT_RELATED_PHRASES),
    name: z.string().min(1, 'Title is required'),
    parameters: z.object({
      nutrientStatement: z.string().optional(),
      nutrient: z.string().min(1, 'Nutrient is required'),
      // Temporary UI fields
      amount: z.string().min(1, 'Amount is required'),
      unit: z.string().min(1, 'Unit is required')
    }).catchall(z.unknown())
  }),
  
  // Avoid Specific Claim Language
  z.object({
    type: z.literal(AVOID_SPECIFIC_CLAIM_LANGUAGE),
    name: z.string().min(1, 'Title is required'),
    parameters: z.object({
      claimType: z.string().min(1, 'Claim type is required'),
      topic: z.string().min(1, 'Topic is required')
    }).catchall(z.unknown())
  }),
  
  // Approved On Package Language
  z.object({
    type: z.literal(APPROVED_ON_PACKAGE_LANGUAGE),
    name: z.string().min(1, 'Title is required'),
    parameters: z.object({
      onPackageText: z.string().min(1, 'On-package text is required')
    }).catchall(z.unknown())
  }),
  
  // Approved Qualifier Phrases Before Claims
  z.object({
    type: z.literal(APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS),
    name: z.string().min(1, 'Title is required'),
    parameters: z.object({
      approvedPhrase: z.string().min(1, 'Approved phrase is required'),
      claim: z.string().min(1, 'Claim is required')
    }).catchall(z.unknown())
  }),
  
  // Approved Product Attribute and Disclaimer Text
  z.object({
    type: z.literal(APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT),
    name: z.string().min(1, 'Title is required'),
    parameters: z.object({
      productAttribute: z.string().min(1, 'Product attribute is required'),
      approvedReplacement: z.string().min(1, 'Approved replacement is required'),
      restrictedText: z.string().min(1, 'Restricted text is required'),
      disclaimer: z.string().min(1, 'Disclaimer is required')
    }).catchall(z.unknown())
  }),
  
  // Avoid Restricted Text for Claims
  z.object({
    type: z.literal(AVOID_RESTRICTED_TEXT_FOR_CLAIMS),
    name: z.string().min(1, 'Title is required'),
    parameters: z.object({
      topic: z.string().min(1, 'Topic is required'),
      referenceText: z.string().min(1, 'Reference text is required')
    }).catchall(z.unknown())
  })
]);

// Infer the type from the schema
export type ModalFormValuesTypes = z.infer<typeof ModalFormSchema>; 